import { Box } from '@amzn/awsui-components-react';
import { CollectionPreferencesProps } from '@amzn/awsui-components-react/polaris/collection-preferences';
import { PropertyFilterProps } from '@amzn/awsui-components-react/polaris/property-filter';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import {
    CopyButton,
    ExtendedFilteringProperty,
    getDisplayValue,
} from '@amzn/event-engine-js-utils';
import React from 'react';
import i18nStrings from 'utils/constants/i18nStrings';
import tableConfig from 'utils/constants/table-config';

import { ParticipantStackOutputTableItem } from './types';

enum ColumnID {
    KEY = 'key',
    VALUE = 'value',
    STACK_NAME = 'stackName',
    DESCRIPTION = 'description',
    SOURCE = 'source',
    TYPE = 'type',
}

export const STORAGE_SCOPE = 'eventOutputsTable_03152023';
export const DEFAULT_PAGINATION_LIMIT = 50;

export const getValueWithCopy = (value: string) => (
    <Box display="inline" margin={{ right: 'xxs' }}>
        <CopyButton variant="inline-icon" content={value} />
        {value}
    </Box>
);

export const defaultPreferences: CollectionPreferencesProps.Preferences = {
    ...tableConfig.defaultPreferences,
    wrapLines: true,
    visibleContent: [
        ColumnID.KEY,
        ColumnID.VALUE,
        ColumnID.STACK_NAME,
        ColumnID.DESCRIPTION,
        ColumnID.TYPE,
    ],
};

type ColumnFieldMap = Record<ColumnID, keyof ParticipantStackOutputTableItem>;

export const columnFieldMap: ColumnFieldMap = {
    [ColumnID.KEY]: 'key',
    [ColumnID.VALUE]: 'value',
    [ColumnID.STACK_NAME]: 'stackName',
    [ColumnID.DESCRIPTION]: 'description',
    [ColumnID.SOURCE]: 'source',
    [ColumnID.TYPE]: 'type',
};

export const defaultSortPreferences: TableProps.SortingState<ParticipantStackOutputTableItem> = {
    ...tableConfig.defaultSortPreferences,
    sortingColumn: {
        sortingField: columnFieldMap[ColumnID.KEY],
    },
};

type ColumnDefinition = TableProps.ColumnDefinition<ParticipantStackOutputTableItem> &
    Partial<CollectionPreferencesProps.VisibleContentOption>;

export const columnDefinitions: ColumnDefinition[] = [
    {
        id: ColumnID.KEY,
        header: i18nStrings.event.eventOutputsTable.columns.key,
        cell: (item) => item[columnFieldMap[ColumnID.KEY]],
        sortingField: columnFieldMap[ColumnID.KEY],
        editable: false,
    },
    {
        id: ColumnID.VALUE,
        header: i18nStrings.event.eventOutputsTable.columns.value,
        cell: (item) => getValueWithCopy(item[columnFieldMap[ColumnID.VALUE]]),
        sortingField: columnFieldMap[ColumnID.VALUE],
    },
    {
        id: ColumnID.STACK_NAME,
        header: i18nStrings.event.eventOutputsTable.columns.stackName,
        cell: (item) => item[columnFieldMap[ColumnID.STACK_NAME]],
        sortingField: columnFieldMap[ColumnID.STACK_NAME],
    },
    {
        id: ColumnID.DESCRIPTION,
        header: i18nStrings.event.eventOutputsTable.columns.description,
        cell: (item) =>
            getDisplayValue(item[columnFieldMap[ColumnID.DESCRIPTION]], {
                defaultValue: i18nStrings.emptyValue,
            }),
        sortingField: columnFieldMap[ColumnID.DESCRIPTION],
    },
    {
        id: ColumnID.SOURCE,
        header: i18nStrings.event.eventOutputsTable.columns.source,
        cell: (item) => item[columnFieldMap[ColumnID.SOURCE]],
        sortingField: columnFieldMap[ColumnID.SOURCE],
    },
    {
        id: ColumnID.TYPE,
        header: i18nStrings.event.eventOutputsTable.columns.type,
        cell: (item) => item[columnFieldMap[ColumnID.TYPE]],
        sortingField: columnFieldMap[ColumnID.TYPE],
    },
];

export const defaultQuery: PropertyFilterProps.Query = {
    tokens: [],
    operation: 'and',
};

export const filteringProperties: ExtendedFilteringProperty[] = [
    {
        key: columnFieldMap[ColumnID.KEY],
        propertyLabel: i18nStrings.event.eventOutputsTable.columns.key,
        groupValuesLabel:
            i18nStrings.event.eventOutputsTable.propertyFilter.key,
        operators: ['=', '!=', ':', '!:'],
        showOptions: false,
    },
    {
        key: columnFieldMap[ColumnID.VALUE],
        propertyLabel: i18nStrings.event.eventOutputsTable.columns.value,
        groupValuesLabel:
            i18nStrings.event.eventOutputsTable.propertyFilter.value,
        operators: ['=', '!=', ':', '!:'],
        showOptions: false,
    },
    {
        key: columnFieldMap[ColumnID.STACK_NAME],
        propertyLabel: i18nStrings.event.eventOutputsTable.columns.stackName,
        groupValuesLabel:
            i18nStrings.event.eventOutputsTable.propertyFilter.stackName,
        operators: ['=', '!=', ':', '!:'],
        showOptions: true,
    },
    {
        key: columnFieldMap[ColumnID.DESCRIPTION],
        propertyLabel: i18nStrings.event.eventOutputsTable.columns.description,
        groupValuesLabel:
            i18nStrings.event.eventOutputsTable.propertyFilter.description,
        operators: ['=', '!=', ':', '!:'],
        showOptions: false,
    },
    {
        key: columnFieldMap[ColumnID.SOURCE],
        propertyLabel: i18nStrings.event.eventOutputsTable.columns.source,
        groupValuesLabel:
            i18nStrings.event.eventOutputsTable.propertyFilter.source,
        operators: ['=', '!=', ':', '!:'],
        showOptions: true,
    },
    {
        key: columnFieldMap[ColumnID.TYPE],
        propertyLabel: i18nStrings.event.eventOutputsTable.columns.type,
        groupValuesLabel:
            i18nStrings.event.eventOutputsTable.propertyFilter.type,
        operators: ['=', '!=', ':', '!:'],
        showOptions: true,
    },
];
